<template>
  <div :class="$style.modal">
    <ModalHeader
      :handleClose="handleClose"
      headerText="Configuration Associations"
      :showClose="true"
    />
    <div :class="$style.modalBody">
      <section
        v-if="shouldShowSelectedText"
        :class="$style.associationContainer"
      >
        <p>
          <MarkdownInline :mdTemplate="selectedText" />
        </p>
      </section>
      <section
        v-if="shouldShowDeselectedText"
        :class="$style.associationContainer"
      >
        <p>
          <MarkdownInline :mdTemplate="unselectedText" />
        </p>
      </section>
      <section v-if="configuration.template.alert">
        <p><strong>{{ configuration.template.alert }}</strong></p>
      </section>
      <section v-if="configuration.template.behavior">
        <strong>{{ configurationItem.title }} Behavior</strong>
        <p>{{ configuration.template.behavior }}</p>
      </section>
      <section v-if="configuration.template.activation">
        <strong>{{ configurationItem.title }} Activation</strong>
        <p>{{ configuration.template.activation }}</p>
      </section>
    </div>
    <ModalFooter
      cancelText="cancel"
      :handleClose="handleClose"
      :handleMainAction="handleContinue"
      :kbLink="configuration.template.reference"
      mainActionText="save"
    />
  </div>
</template>

<script>
import { MarkdownInline } from '@jumpcloud/ui-components';
import { capitalize } from 'lodash';
import Constants, { ItemTableConsts, PolicyConsts } from 'client/util/Constants';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ModalFooter from 'components/Modal/ModalComponents/ModalFooter.vue';
import ModalHeader from 'components/Modal/ModalComponents/ModalHeader.vue';

const { ConfigurationItemType } = ItemType;
const configurationItem = new ConfigurationItemType();

const { iOS } = Constants;

const { policyTemplateDisplayValues, policyTemplateTypes } = PolicyConsts;

const { parentOSType } = ItemTableConsts;
const { ios } = parentOSType;

export default {
  name: 'ConfigurationActivationModal',

  components: {
    MarkdownInline,
    ModalFooter,
    ModalHeader,
  },

  props: {
    configuration: {
      type: Object,
      required: true,
    },
    deselectedPolicyGroupCount: {
      type: Number,
      required: true,
    },
    deselectedSystemCount: {
      type: Number,
      required: true,
    },
    deselectedSystemGroupCount: {
      type: Number,
      required: true,
    },
    handleContinue: {
      type: Function,
      required: true,
    },
    selectedPolicyGroupCount: {
      type: Number,
      required: true,
    },
    selectedSystemCount: {
      type: Number,
      required: true,
    },
    selectedSystemGroupCount: {
      type: Number,
      required: true,
    },
  },

  data: () => ({
    configurationItem,
  }),

  computed: {
    os() {
      const { osMetaFamily } = this.configuration.template;
      if (osMetaFamily !== ios) {
        return capitalize(policyTemplateDisplayValues[osMetaFamily]);
      }
      return iOS;
    },

    selectedText() {
      const { template } = this.configuration;
      let text = `**${template.displayName}** will be applied to`;
      if (this.selectedSystemGroupCount) {
        text = text.concat(` **${this.os} Systems** in **${this.selectedSystemGroupCount}** System Group${this.selectedSystemGroupCount > 1 ? 's' : ''}`);
      }

      if (this.selectedSystemGroupCount
        && template.osMetaFamily === policyTemplateTypes.universal
      ) {
        text = `**${template.displayName}** will be applied to`;
        return text.concat(` **${this.selectedSystemCount} device${this.selectedSystemCount > 1 ? 's' : ''}** in **${this.selectedSystemGroupCount} device group${this.selectedSystemGroupCount > 1 ? 's' : ''}**`);
      }

      if (this.selectedSystemGroupCount && this.selectedSystemCount) {
        text = text.concat(' and to');
      }
      if (this.selectedSystemCount) {
        text = text.concat(` **${this.selectedSystemCount}** individual System${this.selectedSystemCount > 1 ? 's' : ''}`);
      }

      const hasSelectedItems = this.selectedSystemCount || this.selectedSystemGroupCount;
      if (hasSelectedItems && this.selectedPolicyGroupCount) {
        text = text.concat(' and to');
      }
      if (this.selectedPolicyGroupCount) {
        text = text.concat(` **${this.selectedPolicyGroupCount}** Policy Group${this.selectedPolicyGroupCount > 1 ? 's' : ''}`);
      }
      return text;
    },

    shouldShowDeselectedText() {
      return this.deselectedSystemGroupCount || this.deselectedSystemCount
        || this.deselectedPolicyGroupCount;
    },

    shouldShowSelectedText() {
      return this.selectedSystemGroupCount || this.selectedSystemCount
        || this.selectedPolicyGroupCount;
    },

    unselectedText() {
      const { template } = this.configuration;
      let text = `**${template.displayName}** will be disabled on`;
      if (this.deselectedSystemGroupCount) {
        text = text.concat(` **${this.os} Systems** in **${this.deselectedSystemGroupCount}** System Group${this.deselectedSystemGroupCount > 1 ? 's' : ''}`);
      }

      if (this.deselectedSystemGroupCount
        && template.osMetaFamily === policyTemplateTypes.universal
      ) {
        text = `**${template.displayName}** will be disabled on`;
        return text.concat(` **${this.deselectedSystemCount} device${this.deselectedSystemCount > 1 ? 's' : ''}** in **${this.deselectedSystemGroupCount} device group${this.deselectedSystemGroupCount > 1 ? 's' : ''}**`);
      }

      if (this.deselectedSystemGroupCount && this.deselectedSystemCount) {
        text = text.concat(' and on');
      }
      if (this.deselectedSystemCount) {
        text = text.concat(` **${this.deselectedSystemCount}** individual System${this.deselectedSystemCount > 1 ? 's' : ''}`);
      }
      if ((this.deselectedSystemCount || this.deselectedSystemGroupCount)
        && this.deselectedPolicyGroupCount) {
        text = text.concat(' and to');
      }
      if (this.deselectedPolicyGroupCount) {
        text = text.concat(` **${this.deselectedPolicyGroupCount}** Policy Group${this.deselectedPolicyGroupCount > 1 ? 's' : ''}`);
      }
      return text;
    },
  },

  methods: {
    handleClose() {
      ModalEventBus.close();
    },
  },
};
</script>

<style scoped module>
@import "../components/Modal/css/ModalStyles.css";

@value (jcSuccess) from '~css/brand-colors.css';

.associationContainer {
  border-bottom: 1px solid jcSuccess;
  margin-bottom: 20px;
}

.modalBody {
  padding: 28px 35px 0;
}
</style>
